/**
 * Component for the upload of one file to process.
 * Drag'n drop from a file manager is supported.
 *
 * Copyright © Ircam amplify 2024-2025
 */
import {
  ChannelMode,
  InputCondition,
  ModuleMimeType,
} from '@/components/ModuleConfig/ModuleDef'
import { downloader } from '@/config/downloader'
import { logger, LogTag } from '@/config/logger'
import { FileUtil, TextUtil } from '@amplify3/duplo-back-end'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import { fetchAuthSession } from 'aws-amplify/auth'
import { ChangeEvent, DragEvent, useEffect, useRef, useState } from 'react'
import { BiLoaderAlt, BiX } from 'react-icons/bi'
import TipComp from './TipComp'
import { UploadFileInfo } from './UploadFileInfo'

interface InputConditionCompProps {
  // The input condition.
  inputCondition?: InputCondition
  // CSS inline style.
  style?: React.CSSProperties
  // Class name for React CSS.
  className?: string
}

/**
 * A component displaying the input condition: mandatory, optional, or nothing.
 *
 * It is used by the FileUploaderTitle.
 */
const InputConditionComp: React.FC<InputConditionCompProps> = ({
  inputCondition,
  style,
  className,
}) => {
  const conditionText = () => {
    switch (inputCondition) {
      case InputCondition.Mandatory:
        return '* Mandatory'

      case InputCondition.Optional:
        return 'Optional'

      default:
        // Return null if inputCondition is undefined
        return null
    }
  }

  return (
    <span style={style} className={className}>
      {conditionText()}
    </span>
  )
}

interface FileUploaderTitleProps {
  // The input condition.
  inputCondition?: InputCondition
  // The title of the uploader.
  title: string
  // The optional tooltip.
  tip?: string
  // CSS inline style.
  style?: React.CSSProperties
  // Class name for React CSS.
  className?: string
}

/**
 * The title of a FileUploader.
 */
export const FileUploaderTitle: React.FC<FileUploaderTitleProps> = ({
  inputCondition,
  title,
  tip,
  style,
  className,
}) => {
  return (
    <div style={style} className={className}>
      <h4>
        {title} {tip && <TipComp message={tip} />}{' '}
      </h4>
      <InputConditionComp
        className="inputCondition"
        inputCondition={inputCondition}
      />
    </div>
  )
}

export enum FileUploaderDesign {
  Compact,
  Full,
}

export enum UploadState {
  ReadyToUpload, // 0
  AnalyzingFile, // 1
  Uploading, // 2
  StorageCreation, // 3
  Uploaded, // 4
  IasUrlRegistered, // 5
  Complete, // 6
  Error, // 7
}

interface FileUploaderProps {
  id?: string
  title?: string
  text?: string
  mimeTypes: ModuleMimeType[]
  addInputFile: (
    inputId: string,
    fileInfo: UploadFileInfo[] | undefined
  ) => void
  inputFileToUpload: UploadFileInfo | undefined
  inputId: string
  inputData: Record<string, UploadFileInfo>
  tip?: string
  isMultipleSelection?: boolean
  inputCondition?: InputCondition
  parentClass?: string
  showStatus?: boolean
  customMsgComp?: React.ReactNode | null | undefined
  design?: FileUploaderDesign
  fullSecondaryMsg?: React.ReactNode | null | undefined
  statusChangeListener?: (
    status: UploadState,
    msg: (string | JSX.Element)[]
  ) => void
  uploadIcon?: React.ReactNode | null | undefined
  withUploadIcon?: boolean
  resetStatus?: number
  disabled?: boolean
}

/**
 * The file uploader component.
 */
function FileUploader({
  id = undefined,
  tip = '',
  title,
  mimeTypes,
  text = '',
  inputData,
  addInputFile,
  inputFileToUpload,
  inputId,
  isMultipleSelection,
  inputCondition,
  parentClass,
  showStatus = true,
  customMsgComp = undefined,
  design = FileUploaderDesign.Compact,
  fullSecondaryMsg = undefined,
  statusChangeListener = undefined,
  uploadIcon = undefined,
  withUploadIcon = true,
  resetStatus,
  disabled = false,
}: FileUploaderProps) {
  const dropZoneRef = useRef<any>(null)
  const fileDialogRef = useRef<any>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadState, setUploadState] = useState(UploadState.ReadyToUpload)
  const [statusMessage, setStatusMessage] = useState<(string | JSX.Element)[]>(
    []
  )
  const msgTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    setUploadState(UploadState.ReadyToUpload)
  }, [resetStatus])

  // Logs
  useEffect(() => {
    logger.debug(
      LogTag.FileUploader,
      `[${id ?? ''}][${inputId}] inputData:`,
      inputData
    )
  }, [inputData])

  useEffect(() => {
    logger.debug(
      LogTag.FileUploader,
      `[${id ?? ''}][${inputId}] inputFileToUpload:`,
      inputFileToUpload
    )
  }, [inputFileToUpload])

  useEffect(() => {
    let strState = ''
    switch (uploadState) {
      case UploadState.ReadyToUpload:
        strState = 'ReadyToUpload'
        break
      case UploadState.AnalyzingFile:
        strState = 'AnalyzingFile'
        break
      case UploadState.Uploading:
        strState = 'Uploading'
        break
      case UploadState.StorageCreation:
        strState = 'StorageCreation'
        break
      case UploadState.Uploaded:
        strState = 'Uploaded'
        break
      case UploadState.IasUrlRegistered:
        strState = 'IasUrlRegistered'
        break
      case UploadState.Complete:
        strState = 'Complete'
        break
      case UploadState.Error:
        strState = 'Error'
        break
      default:
        strState = 'unknown'
    }

    logger.debug(
      LogTag.FileUploader,
      `[${id ?? ''}][${inputId}] uploadState: (${uploadState}) ${strState}`
    )
    if (statusChangeListener) {
      statusChangeListener(uploadState, statusMessage)
    }
  }, [uploadState, statusChangeListener])

  /**
   * Clear the status message.
   */
  const clearStatusMessage = () => {
    setStatusMessage([])
    if (msgTimeoutRef.current) {
      clearTimeout(msgTimeoutRef.current)
    }
  }

  const isReadyToUpload = () => {
    return [
      UploadState.ReadyToUpload,
      UploadState.Complete,
      UploadState.Error,
    ].includes(uploadState)
  }

  function getFileListFromDragEvent(event: DragEvent<HTMLDivElement>) {
    const dt = event.dataTransfer
    const { files } = dt
    return files
  }

  /**
   * Handle the drag'n drop of a file.
   */
  const handleFileDrop = (event: DragEvent<HTMLDivElement>) => {
    if (isReadyToUpload()) {
      dropZoneRef.current.style.opacity = 1
      const files = getFileListFromDragEvent(event)
      if (files) {
        handleFileSelected(files)
      }
    }
  }

  /**
   *  Handle the click on the text 'click to browse'.
   */
  const handleBrowse = () => {
    try {
      const input = fileDialogRef.current
      if (input) {
        // Clear previous selection to be sure that the onChange handler
        // is always called
        input.value = ''

        // Open the hidden file dialog
        input.click()
      }
    } catch (error) {
      logger.error(LogTag.FileUploader, `[${inputId}] Error:`, error)
    }
  }

  const handleRemoveFile = () => {
    addInputFile(inputId, undefined)
    setUploadState(UploadState.ReadyToUpload)
    clearStatusMessage()
  }

  /**
   *  Handle the selection of the file to upload, from the file input dialog.
   */
  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault()

    if (event.target.files?.length) {
      handleFileSelected(event.target.files)
    }

    // Reset file input
    event.target.value = ''
  }

  const handleFileSelected = (fileList: FileList) => {
    clearStatusMessage()

    // Create a list of UploadFileInfo from the list of File.
    const uploadFileInfos: UploadFileInfo[] = Array.from(fileList).map((file) =>
      UploadFileInfo.fromLocalFile(file)
    )
    addInputFile(inputId, uploadFileInfos)
    setUploadState(UploadState.AnalyzingFile)
  }

  /**
   * Upload file to Storage module when fileToUpload is ready.
   */
  useEffect(() => {
    if (!inputFileToUpload) {
      // Do nothing
      return
    }

    if (
      UploadState.AnalyzingFile === uploadState &&
      !inputFileToUpload.isUploaded &&
      inputFileToUpload.requirementCheck
    ) {
      logger.debug(
        LogTag.FileUploader,
        `[${id ?? ''}][${inputId}] File to upload: ${inputFileToUpload.localFile?.name}`,
        inputFileToUpload
      )
      if (inputFileToUpload.requirementCheck.isValid() !== false) {
        // File is valid and ready to be uploaded
        setIsUploading(true)
        setUploadState(UploadState.Uploading)
      } else {
        // Not a valid file.
        logger.warning(
          LogTag.FileUploader,
          `[${id ?? ''}][${inputId}] Invalid file.`
        )
        setUploadState(UploadState.Error)
        setIsUploading(false)
      }
    }
  }, [inputFileToUpload, uploadState])

  useEffect(() => {
    if (UploadState.Uploading === uploadState && inputFileToUpload?.localFile) {
      const localFile = inputFileToUpload.localFile
      fetchAuthSession().then((session) => {
        // downloader.log(session)
        downloader
          .asyncUpload(session, localFile)
          .then((uploadedIasId) => {
            const updatedFile = new UploadFileInfo(
              inputFileToUpload.mimetype,
              inputFileToUpload.localFile,
              true, // Update the isUploaded property
              { ias: `ias://${uploadedIasId}` },
              inputFileToUpload.fileName,
              inputFileToUpload.extension,
              inputFileToUpload.size,
              inputFileToUpload.requirementCheck
            )
            addInputFile(inputId, [updatedFile])

            setUploadState(UploadState.IasUrlRegistered)
            setIsUploading(false)
          })
          .catch((error) => {
            setStatusMessage([`${error}`])
            setUploadState(UploadState.Error)
            setIsUploading(false)
          })
      })
    }
  }, [inputFileToUpload, uploadState])

  useEffect(() => {
    logger.debug(
      LogTag.FileUploader,
      `[${id ?? ''}][${inputId}] inputFileToUpload:`,
      inputFileToUpload
    )
    if (
      isReadyToUpload() &&
      inputFileToUpload &&
      !inputFileToUpload.isUploaded
    ) {
      setUploadState(UploadState.AnalyzingFile)
      clearStatusMessage()
    }
  }, [inputFileToUpload])

  /**
   * Update the list of input files in the parent component.
   */
  useEffect(() => {
    if (inputFileToUpload && UploadState.IasUrlRegistered == uploadState) {
      setIsUploading(false)
      setUploadState(UploadState.Complete)
    }
  }, [inputFileToUpload, uploadState])

  /**
   * Display error message depending on the status of the requirements checking.
   */
  useEffect(() => {
    const checking = inputFileToUpload?.requirementCheck
    if (checking) {
      const errorMsgList: (string | JSX.Element)[] = []

      if (false === checking.isTypeValid) {
        errorMsgList.push('Unsupported file type.')
      }

      // Minimum duration error message
      if (false === checking.isMinDurationValid) {
        let minDurationMsg = 'Audio duration is too short'

        const minDuration = checking.requirements.minDuration
        if (minDuration) {
          minDurationMsg += `, must be at least ${minDuration}s.`
        } else {
          minDurationMsg += '.'
        }

        errorMsgList.push(minDurationMsg)
      }

      // Maximum duration error message
      if (false === checking.isMaxDurationValid) {
        let maxDurationMsg = 'Audio duration is too long'

        const maxDuration = checking.requirements.maxDuration
        if (maxDuration) {
          maxDurationMsg += `, must not exceed ${TextUtil.formatDuration(
            maxDuration
          )}.`
        } else {
          maxDurationMsg += '.'
        }

        errorMsgList.push(maxDurationMsg)
      }

      // Channel mode error message
      if (false === checking.isChannelCountValid) {
        let modeMsg = 'Unsupported channel mode'

        const modeList = checking.requirements.channelModeList
        if (modeList) {
          modeMsg += ', support only: '
          modeList.map(
            (mode: ChannelMode, index: number) =>
              (modeMsg += index > 0 ? ',' : '' + mode)
          )
        }
        modeMsg += '.'
        errorMsgList.push(modeMsg)
      }

      // Display error message
      if (errorMsgList.length > 0) {
        // Call setStatusMessage instead of displayStatusMessage
        // to avoid automic clearing.
        setStatusMessage(errorMsgList)
      }
    }
  }, [inputFileToUpload])

  /**
   * The component displaying the status message.
   */
  const StatusMessageComp = () => {
    return (
      <div className="fileUploaderAccentText">
        {
          /* Display each error message in one line. */
          statusMessage.map((item, index) => (
            <div key={index} className="statusItem">
              {item}
            </div>
          ))
        }
      </div>
    )
  }

  /**
   * Component displaying information about the selected file.
   * @param inputFileToUpload The information of the file.
   */
  const SelectedFileComp = ({
    inputFileToUpload,
    disabled,
  }: {
    inputFileToUpload: UploadFileInfo
    disabled: boolean
  }) => {
    return (
      <div>
        <div className="uploadInfos">
          <div className="flex flex-col justify-center items-start">
            <div className="truncate max-w-[370px]">
              {inputFileToUpload.fileName}
            </div>
            {inputFileToUpload.size && (
              <div className="uploadInfos2">
                {FileUtil.strSizeInKb(inputFileToUpload.size)} kB &nbsp;&nbsp;|
                &nbsp;&nbsp;
                {inputFileToUpload.extension}
              </div>
            )}
          </div>

          <button
            id="buttonRemoveFile"
            className="linkBtn"
            type="button"
            aria-label="Remove file"
            onClick={handleRemoveFile}
            disabled={disabled}
          >
            <BiX />
          </button>
        </div>

        <StatusMessageComp />
      </div>
    )
  }

  const DropFileCompactComp = ({ disabled }: { disabled: boolean }) => {
    return (
      <div>
        Drop a file here, or{' '}
        <span className="browseLink">
          <button
            onClick={handleBrowse}
            type="button"
            className="linkBtn"
            disabled={disabled}
          >
            click to browse
          </button>
        </span>
      </div>
    )
  }

  const DropFileFullComp = ({ disabled }: { disabled: boolean }) => {
    return (
      <div className="flex flex-col items-center">
        {withUploadIcon &&
          (uploadIcon ?? <CloudUploadIcon className="fullIcon" />)}

        <div className="fullPrimaryMsg">
          Drop a file here, or{' '}
          <span className="browseLink">
            <button
              onClick={handleBrowse}
              type="button"
              className="linkBtn"
              disabled={disabled}
            >
              click to browse
            </button>
          </span>
        </div>

        {fullSecondaryMsg && (
          <div className="fullSecondaryMsg">{fullSecondaryMsg}</div>
        )}
      </div>
    )
  }

  /**
   * Text inviting to drag'n drop a file
   * or to open a file dialog.
   */
  const DropFileComp = ({
    customMsgComp,
    design,
    disabled,
  }: {
    customMsgComp?: React.ReactNode
    design: FileUploaderDesign
    disabled: boolean
  }) => {
    return (
      <div className="DropFileComp" id={`DropFileComp_${id}`}>
        <div className="DropFileComp2">
          {
            // Display custom message
            customMsgComp ??
              (FileUploaderDesign.Full === design ? (
                // Display the full version
                <DropFileFullComp disabled={disabled} />
              ) : (
                // Display the compact version by default
                <DropFileCompactComp disabled={disabled} />
              ))
          }
        </div>
      </div>
    )
  }

  const getDropZoneClass = (additionalCss: string = '') => {
    return `dropZone fileUploader ${parentClass ?? ''} ${additionalCss ?? ''}`
  }

  const blockIfDisabled = (e: React.DragEvent) => {
    if (disabled) {
      e.preventDefault()
      e.stopPropagation()
      return true
    }
    return false
  }

  return (
    <div
      ref={dropZoneRef}
      className={getDropZoneClass()}
      onDrop={(e) => {
        if (blockIfDisabled(e)) return

        logger.debug(
          LogTag.FileUploader,
          `[${id ?? ''}]`,
          `onDrop:`,
          getFileListFromDragEvent(e)
        )
        e.stopPropagation()
        e.preventDefault()
        if (isReadyToUpload()) {
          handleFileDrop(e)
          dropZoneRef.current.className = getDropZoneClass()
        } else {
          logger.debug(
            LogTag.FileUploader,
            `[${id ?? ''}]`,
            'onDrop: Currently uploading another file, ignore this file drop.'
          )
        }
      }}
      onDragEnter={(e) => {
        if (blockIfDisabled(e)) return

        logger.debug(LogTag.FileUploader, `[${id ?? ''}]`, 'onDragEnter')
        e.stopPropagation()
        e.preventDefault()
        if (isReadyToUpload()) {
          dropZoneRef.current.style.opacity = 0.7
          dropZoneRef.current.className = getDropZoneClass('drag')
        }
      }}
      onDragLeave={(e) => {
        if (blockIfDisabled(e)) return

        e.stopPropagation()
        e.preventDefault()
        // Ensure the mouse is truly outside the dropZone
        // and not over a child element.
        // This is needed to avoid the background reset
        // to white color when hovering above the child uploadDiv element.

        // Get the bounding rectangle of the drop zone
        const rect = dropZoneRef.current.getBoundingClientRect()

        // Check if the mouse pointer is outside the drop zone
        const isOutside =
          e.clientX < rect.left ||
          e.clientX > rect.right ||
          e.clientY < rect.top ||
          e.clientY > rect.bottom

        if (isOutside) {
          dropZoneRef.current.className = getDropZoneClass() // Reset class
          logger.debug(
            LogTag.FileUploader,
            `[${id ?? ''}]`,
            'onDragleave isOutside'
          )
        }
      }}
      onDragOver={(e) => {
        if (blockIfDisabled(e)) return

        e.stopPropagation()
        e.preventDefault()
      }}
    >
      {title && (
        <FileUploaderTitle
          className="titleBar"
          inputCondition={inputCondition}
          title={title}
          tip={tip}
        />
      )}

      {Boolean(text) && <div>{text}</div>}
      {showStatus ? (
        <div className={`uploadDiv`}>
          {isUploading || uploadState === UploadState.AnalyzingFile ? (
            // Display a circular progress icon with a message
            <div className="smallLoadingDiv">
              <BiLoaderAlt className="loadingIcon" />
              {isUploading ? 'Uploading...' : 'Analyzing...'}
            </div>
          ) : (
            <div className="flex flex-col">
              {inputFileToUpload?.fileName ? (
                <SelectedFileComp
                  inputFileToUpload={inputFileToUpload}
                  disabled={disabled}
                />
              ) : (
                UploadState.Complete !== uploadState && (
                  <DropFileComp
                    customMsgComp={customMsgComp}
                    design={design}
                    disabled={disabled}
                  />
                )
              )}
            </div>
          )}

          {/*
          // TODO: LastUploads must be fixed
         {!(UploadState.Complete != uploadState)
          && !isUploading
          && !inputId.includes('lyrics')
          && !inputFileToUpload?.isUploaded
          && (
            <LastUploads
              setIsUploading={setIsUploading}
              inputData={inputData}
              addInputFile={addInputFile}
              inputId={inputId}
              setFile={setFileToUpload}
              setCompletedUploadsList={setCompletedUploadsList}
              setTempStorageId={setIasId}
            />
          )} */}
        </div>
      ) : (
        <DropFileComp
          customMsgComp={customMsgComp}
          design={design}
          disabled={disabled}
        />
      )}

      {/**
       * A hidden file input dialog. This dialog will be shown
       * when the user will click on the browse link.
       */}
      <input
        id={`input_${id}`}
        style={{ display: 'none' }}
        ref={fileDialogRef}
        type="file"
        accept={mimeTypes.join(',')}
        onChange={handleFileChange}
        multiple={isMultipleSelection ?? true}
        disabled={disabled}
      />
    </div>
  )
}

export default FileUploader
